import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { AnalyticsResultsService, TransactionCategoryStatistic } from 'src/app/shared/services/analytics-results.service';

@Component({
  selector: 'app-txn-categories-stats-table',
  templateUrl: './txn-categories-stats-table.component.html',
  styleUrls: ['../widgets.scss', './txn-categories-stats-table.component.scss']
})
export class TxnCategoriesStatsTableComponent implements OnInit, AfterViewInit {
  constructor(private resultsAnalyticsSvc: AnalyticsResultsService, private breakpointObserver: BreakpointObserver) {
    breakpointObserver.observe([
      Breakpoints.Handset
    ]).subscribe(result => {
      if (result.matches) {
        this.isMobile = true;
        this.displayedColumns = ['categoryName', 'lastMonth', 'thisMonth'];
      } else {
        this.isMobile = false;
        this.displayedColumns = ['categoryName', '12months', 'ytd', 'avgPerMonth', 'lastMonth', 'thisMonth'];
      }
      this.sort();
    });
  }

  currentYearTxnCats: TransactionCategoryStatistic[] = new Array<TransactionCategoryStatistic>();
  categoryStats = new MatTableDataSource<TxnCategoryTableRow>();
  displayedColumns: string[] = ['categoryName', '12months', 'ytd', 'avgPerMonth', 'lastMonth', 'thisMonth'];
  isMobile: boolean = false;
  thisMonthLabel: string = "";
  lastMonthLabel: string = "";
  ytdLabel: string = "";

  @ViewChild(MatPaginator) paginator = new MatPaginator(new MatPaginatorIntl(), ChangeDetectorRef.prototype);

  ngOnInit(): void {
    this.resultsAnalyticsSvc.getCategoriesForTransactionsLast12Months()
      .subscribe({
        next: (data) => {
          this.currentYearTxnCats = data;
          this.generateStats();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status == 400) {
          } else {
            throw err;
          }
        }
      });
  }

  ngAfterViewInit() {
    this.categoryStats.paginator = this.paginator;
  }

  generateStats(): void {
    if (!this.currentYearTxnCats || this.currentYearTxnCats.length == 0) {
      return;
    }

    let now = new Date(Date.now());
    let lastMonthDate = this.addMonths(now, -1);

    let thisMonth = this.getMonthYearNumber(now);
    let lastMonth = this.getMonthYearNumber(lastMonthDate);
    let startOfYear = this.getMonthYearNumber(this.setStartOfYear(now));
    let last12Months = this.getMonthYearNumber(this.addMonths(now, -12));

    this.thisMonthLabel = now.toLocaleString('default', { month: 'short' });
    this.ytdLabel = now.toLocaleString('default', { year: 'numeric' });
    this.lastMonthLabel = lastMonthDate.toLocaleString('default', { month: 'short' });

    let stats = new Array<TxnCategoryTableRow>();
    let monthCount = new Array<number>();

    this.currentYearTxnCats.forEach((x) => {
      if (x.dim_txndate_yearmonth as number >= last12Months) {
        let statIndex = this.getIndexOfGroup(stats, x.dim_txncat);

        if (monthCount.findIndex((y) => y === x.dim_txndate_yearmonth) < 0) {
          monthCount.push(x.dim_txndate_yearmonth as number);
        }

        if (statIndex < 0) {
          let txnGrp = new TxnCategoryTableRow();
          txnGrp.categoryName = x.dim_txncat;
          statIndex = stats.push(txnGrp) - 1;
        }

        if (x.dim_txndate_yearmonth === thisMonth) {
          stats[statIndex].thisMonthValue = stats[statIndex].thisMonthValue + (x.fct_sum_abs_amount as number);
        }

        if (x.dim_txndate_yearmonth === lastMonth) {
          stats[statIndex].lastMonthValue = stats[statIndex].lastMonthValue + (x.fct_sum_abs_amount as number);
        }

        if (x.dim_txndate_yearmonth as number >= startOfYear && x.dim_txndate_yearmonth as number <= thisMonth) {
          stats[statIndex].ytdValue = stats[statIndex].ytdValue + (x.fct_sum_abs_amount as number);
        }

        if (x.dim_txndate_yearmonth as number >= last12Months && x.dim_txndate_yearmonth as number <= thisMonth) {
          stats[statIndex].last12MonthsValue = stats[statIndex].last12MonthsValue + (x.fct_sum_abs_amount as number);
        }

      }

    });

    let numberOfMonths = monthCount.length;
    if (numberOfMonths !== 0) {
      stats.forEach((x, i) => {
        stats[i].avgPerMonth = stats[i].last12MonthsValue / numberOfMonths;
      });
    }

    this.categoryStats.data = stats;
    this.sort();
  }

  sort() {
    if (this.categoryStats && this.categoryStats.data && this.categoryStats.data.length > 0) {
      if (this.isMobile === true) {
        this.categoryStats.data = this.categoryStats.data.sort(this.sortStatsHighestSpendFirstLastMonth);
      } else {
        this.categoryStats.data = this.categoryStats.data.sort(this.sortStatsHighestSpendFirst12m);
      }
    }
  }

  addMonths(at: Date, months: number): Date {
    let d = new Date(at);
    d.setMonth(d.getMonth() + months);

    return d;
  }

  setStartOfYear(at: Date): Date {
    let d = new Date(at);
    d.setMonth(0); // 0 = January
    return d;
  }

  getMonthYearNumber(at: Date): number {
    return (at.getFullYear() * 100) + at.getMonth() + 1;
  }

  getIndexOfGroup(data: TxnCategoryTableRow[], category: string): number {
    return data.findIndex((y) => {
      if (y.categoryName === category) {
        return true;
      }
      return false;
    });
  }

  sortStatsHighestSpendFirst12m(a: TxnCategoryTableRow, b: TxnCategoryTableRow): number {
    if (a.last12MonthsValue > b.last12MonthsValue) {
      return -1;
    } else if (a.last12MonthsValue < b.last12MonthsValue) {
      return 1;
    }

    return 0;
  }

  sortStatsHighestSpendFirstLastMonth(a: TxnCategoryTableRow, b: TxnCategoryTableRow): number {
    if (a.lastMonthValue > b.lastMonthValue) {
      return -1;
    } else if (a.lastMonthValue < b.lastMonthValue) {
      return 1;
    }

    return 0;
  }
}

export class TxnCategoryTableRow {
  public categoryName: string = "";
  public thisMonthValue: number = 0;
  public lastMonthValue: number = 0;
  public ytdValue: number = 0;
  public last12MonthsValue: number = 0;
  public avgPerMonth: number = 0;
}

